import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment";
import { AgGridReact } from "ag-grid-react";
import { toast } from "react-toastify";
import {
  FaCheck,
  FaEdit,
  FaPlus,
  FaChevronDown,
  FaChevronUp,
  FaMoneyBillWave,
  FaHistory,
  FaReceipt,
  FaFileAlt,
  FaExchangeAlt,
  FaLock,
  FaLockOpen,
  FaArrowLeft,
} from "react-icons/fa";
import { MdOutlineApproval } from "react-icons/md";
import { getWorkOrderById } from "../../../../Redux/actions/Operationsactions/getworkorderbyid";
import { UpdateWOBApprovalApi, AddWOPaymentApi } from "../../../../Api/Operations/WorkOrders";
import { useSession } from "../../../../context/SessionContext";
import StatusChip, { statusChipType } from "../../../Common/StatusChip";
import DeleteConfirmModal from "../../../../Common/DeleteConfirmModal";
import { fetchGeneralLedgerTransactions } from "../../../../Redux/actions/AccountReports/reports";
import TransactionDetailModal from "../../../../Common/TransactionDetailModal";
import ActionButton from "../../../Common/ActionButton";
import AddBillModal from "./AddBillModal";
import AddAmendmentModal from "./AddAmendmentModal";
import RetentionReleaseModal from "./RetentionReleaseModal";

const StatusBadge = ({ status }) => <StatusChip status={status} type={statusChipType(status)} />;

const ColoredActionButton = ({
  icon: Icon,
  label,
  onClick,
  variant = "primary",
  disabled = false,
}) => {
  const variants = {
    primary: { bg: "#0b6462", hover: "#095250", shadow: "rgba(11,100,98,0.25)", text: "white" },
    danger: { bg: "#c0392b", hover: "#a93226", shadow: "rgba(192,57,43,0.25)", text: "white" },
    success: { bg: "#0e8c6e", hover: "#0a7058", shadow: "rgba(14,140,110,0.25)", text: "white" },
    info: { bg: "#0097a7", hover: "#00838f", shadow: "rgba(0,151,167,0.25)", text: "white" },
    warning: { bg: "#e07b00", hover: "#c96e00", shadow: "rgba(224,123,0,0.25)", text: "white" },
    secondary: { bg: "#546e7a", hover: "#455a64", shadow: "rgba(84,110,122,0.25)", text: "white" },
    save: { bg: "#0b6462", hover: "#095250", shadow: "rgba(11,100,98,0.25)", text: "white" },
  };

  const v = variants[variant] || variants.primary;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="btn d-flex align-items-center justify-content-center"
      style={{
        padding: "6px 12px",
        borderRadius: "8px",
        fontSize: "12px",
        fontWeight: "600",
        whiteSpace: "nowrap",
        transition: "all 0.2s ease",
        backgroundColor: v.bg,
        color: v.text,
        border: "none",
        boxShadow: `0 2px 8px ${v.shadow}`,
        opacity: disabled ? 0.55 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        gap: "5px",
      }}
      title={label}
      onMouseEnter={(e) => {
        if (disabled) {
          return;
        }
        e.currentTarget.style.backgroundColor = v.hover;
        e.currentTarget.style.boxShadow = `0 4px 12px ${v.shadow}`;
      }}
      onMouseLeave={(e) => {
        if (disabled) {
          return;
        }
        e.currentTarget.style.backgroundColor = v.bg;
        e.currentTarget.style.boxShadow = `0 2px 8px ${v.shadow}`;
      }}
    >
      <Icon size={12} />
      <span className="d-none d-md-inline">{label}</span>
    </button>
  );
};

const SectionHeader = ({ icon: Icon, title, rightElement }) => (
  <div
    className="d-flex align-items-center justify-content-between mb-3 pb-2"
    style={{ borderBottom: "2px solid #e7f4f4" }}
  >
    <div className="d-flex align-items-center">
      <div
        className="d-flex align-items-center justify-content-center me-2"
        style={{
          width: "36px",
          height: "36px",
          borderRadius: "10px",
          backgroundColor: "#e7f4f4",
          color: "#0b6462",
        }}
      >
        <Icon size={20} />
      </div>
      <h6 className="m-0 ml-2" style={{ fontWeight: "700", letterSpacing: "-0.5px" }}>
        {title}
      </h6>
    </div>
    {rightElement}
  </div>
);

const DetailRow = ({ type, label, value }) => {
  if (type === "description") {
    return (
      <div className="col-12 mb-2">
        <div className="text-secondary fz-12" style={{ fontWeight: 500, marginBottom: "2px" }}>
          {label}
        </div>
        <div className="text-dark fz-13" style={{ fontWeight: 600, wordBreak: "break-word" }}>
          {value}
        </div>
      </div>
    );
  }
  return (
    <div className="col-6 mb-2">
      <div className="text-secondary fz-12" style={{ fontWeight: 500, marginBottom: "2px" }}>
        {label}
      </div>
      <div className="text-dark fz-13" style={{ fontWeight: 600, wordBreak: "break-word" }}>
        {value}
      </div>
    </div>
  );
};

export default function WorkOrderDetailsPage() {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const { session } = useSession();

  const reduxState = useSelector((s) => s.getWorkOrderDetails || { data: null, loading: true });
  const generalLedger = useSelector((state) => state.generalLedger);

  const [isLoading, setIsLoading] = useState(false);
  const [showBillModal, setShowBillModal] = useState(false);
  const [showAmendmentModal, setShowAmendmentModal] = useState(false);
  const [showRetentionModal, setShowRetentionModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [confirmationType, setConfirmationType] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [openTransactionModel, setOpenTransactionModel] = useState(false);
  const [approveComments, setApproveComments] = useState("");
  const [selectedApprovalId, setSelectedApprovalId] = useState(null);
  const [expandedBills, setExpandedBills] = useState({});
  const [selectedBillId] = useState(null);
  // const [selectedBillId, setSelectedBillId] = useState(null);

  const toggleBill = (billId) => {
    setExpandedBills((prev) => ({ ...prev, [billId]: !prev[billId] }));
  };

  useEffect(() => {
    if (!id) {
      return;
    }
    dispatch(getWorkOrderById(id));
  }, [id, dispatch]);

  const current = reduxState?.data?.Data || null;

  const openEdit = useCallback(() => {
    history.push("/store/contractors/work-order/add", current);
  }, [history, current]);

  const getTransactions = (txId) => {
    dispatch(fetchGeneralLedgerTransactions(txId));
  };

  const openApprove = (approvals) => {
    if (!approvals?.length) {
      return;
    }
    const pending = (approvals || []).find((a) => a?.ApproverUserId === session?.LoginId);
    const approvalId = pending?.Id;
    if (!approvalId) {
      toast.info("No pending approval found");
      return;
    }
    setSelectedApprovalId(approvalId);
    setApproveComments("");
    setShowApproveModal(true);
  };

  const handleApproveBill = async () => {
    setIsLoading(true);
    try {
      const res = await UpdateWOBApprovalApi(selectedApprovalId, "Approved", approveComments || "");
      if (res?.data?.IsSuccess) {
        toast.success("Bill approved");
        setShowApproveModal(false);
        setSelectedApprovalId(null);
        setApproveComments("");
        dispatch(getWorkOrderById(id));
      } else {
        toast.error(res?.data?.Message || "Failed to approve bill");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error approving bill");
    } finally {
      setIsLoading(false);
      setShowApproveModal(false);
      setShowConfirmationModal(false);
      setConfirmationType(null);
    }
  };

  const handleAddPayment = async () => {
    setIsLoading(true);
    try {
      const res = await AddWOPaymentApi(selectedBillId);
      if (res?.data?.IsSuccess) {
        toast.success("Payment created");
        await dispatch(getWorkOrderById(id));
      } else {
        toast.error(res?.data?.Message || "Failed to add payment");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error adding payment");
    } finally {
      setIsLoading(false);
      setShowConfirmationModal(false);
      setConfirmationType(null);
    }
  };

  const activityRows = useMemo(() => {
    if (!current) {
      return [];
    }
    const rows = [];
    if (current.CreatedAt) {
      rows.push({
        dateTime: current.CreatedAt,
        action: "Work Order Created",
        description: current.Description || "",
        user: current.CreatedBy || "",
      });
    }
    (current.Bills || []).forEach((b) => {
      rows.push({
        dateTime: b.CreatedAt,
        action: `Bill Created: ${b.BillNumber || ""}`,
        description: `Cumulative: ${b.CumulativePercentage}%`,
        user: b.CreatedBy || "",
      });
      b.WobApprovals?.forEach((a) =>
        rows.push({
          dateTime: a.ActionDate || a.CreatedAt || new Date().toISOString(),
          action: `${a.Status?.toUpperCase() === "PENDING" ? "Pending Approval" : a.Status}: ${b.BillNumber || "Bill"}`,
          description: a.Comments || "",
          user: a.ApprovalUserName || "",
        }),
      );
    });
    (current.Amendments || []).forEach((a) => {
      rows.push({
        dateTime: a.AmendmentDate,
        action: "Amendment Added",
        description: a.Reason || "",
        user: a.CreatedBy || "",
      });
    });
    return rows.sort((x, y) => new Date(y.dateTime) - new Date(x.dateTime));
  }, [current]);

  const activityColDefs = useMemo(
    () => [
      {
        headerName: "Date/Time",
        field: "dateTime",
        flex: 1,
        valueFormatter: (p) => (p.value ? moment(p.value).format("YYYY-MM-DD HH:mm") : ""),
      },
      { headerName: "Action", field: "action", flex: 1 },
      { headerName: "Description", field: "description", flex: 2 },
      { headerName: "User", field: "user", flex: 1 },
    ],
    [],
  );
  const taxAmount = (current?.TotalContractValue * current?.TaxRate) / 100 || 0;
  return (
    <div className="container-fluid p-3 anim-fade-in">
      <style>
        {`
          .anim-fade-in {
            animation: fadeIn 0.4s ease-out;
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .bill-card {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          }
          .bill-card:hover {
            border-color: #0b6462 !important;
            box-shadow: 0 8px 20px rgba(11, 100, 98, 0.12) !important;
            transform: translateY(-2px);
          }
        `}
      </style>

      <div className="row mb-3">
        <div className="col-12 page_heading">
          <div className="d-flex align-items-center">
            <FaArrowLeft
              size={16}
              style={{ cursor: "pointer", marginRight: "12px" }}
              title="Back"
              onClick={() => history.goBack()}
            />
            <h4 className="m-0">Work Order Details</h4>
          </div>
        </div>
      </div>

      {/* Top Row: Info Card + Activity */}
      <div className="row mb-3">
        <div className="col-md-5 mt-2">
          <div
            className="p-3 h-100"
            style={{
              backgroundColor: "#fafafa",
              borderRadius: "6px",
              boxShadow: "0px 2px 4px rgba(0,0,0,0.04)",
              border: "1px solid #e0e0e0",
            }}
          >
            <h6
              className="section-heading mb-2 fz-15 d-flex align-items-center justify-content-between"
              style={{
                color: "#0b6462",
                borderBottom: "1px solid #caf5f5",
                paddingBottom: "8px",
                paddingTop: "4px",
                marginBottom: "10px",
              }}
            >
              <div
                className="d-flex align-items-center"
                style={{ whiteSpace: "nowrap", flexShrink: 0 }}
              >
                <div
                  className="d-flex align-items-center justify-content-center me-2"
                  style={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "8px",
                    backgroundColor: "#e7f4f4",
                    color: "#0b6462",
                    flexShrink: 0,
                  }}
                >
                  <FaFileAlt size={16} />
                </div>
                <b className="ml-1" style={{ whiteSpace: "nowrap" }}>
                  Work Order
                </b>
              </div>
              <div
                className="ms-auto d-flex align-items-center"
                style={{ gap: "10px", marginLeft: "12px" }}
              >
                {current?.Status === "Pending" &&
                  current?.Bills?.length === 0 &&
                  current?.Amendments?.length === 0 && (
                    <ColoredActionButton
                      icon={FaEdit}
                      label="Edit WO"
                      variant="primary"
                      onClick={openEdit}
                    />
                  )}
              </div>
            </h6>
            <div className="row mx-0 mt-2">
              <DetailRow label="Contractor" value={current?.ContractorName} />
              <DetailRow label="Status" value={<StatusBadge status={current?.Status || "N/A"} />} />
              <DetailRow
                label="Original Amount"
                value={`Rs. ${current?.OriginalAmount?.toLocaleString?.() || "0"}`}
              />
              <DetailRow
                label="Contract Value"
                value={`Rs. ${current?.TotalContractValue?.toLocaleString?.() || "0"}`}
              />
              <DetailRow
                label="Total Payble"
                value={`Rs. ${(current?.TotalContractValue - taxAmount)?.toLocaleString?.() || "0"}`}
              />
              <DetailRow
                label="Pending Value"
                value={`Rs. ${current?.TotalPendingValue?.toLocaleString?.() || "0"}`}
              />

              <DetailRow label="Retention Rate" value={`${current?.RetentionRate || 0}%`} />
              <DetailRow
                label="Retention Amount"
                value={`Rs. ${(
                  (current?.TotalContractValue * current?.RetentionRate) / 100 || "0"
                ).toLocaleString?.()}`}
              />

              <DetailRow label="Tax Rate" value={`${current?.TaxRate || 0}%`} />
              <DetailRow label="Tax Amount" value={`Rs. ${taxAmount?.toLocaleString?.() || "0"}`} />
              <DetailRow
                type="description"
                label="Description"
                value={current?.Description || "-"}
              />
            </div>
          </div>
        </div>
        <div className="col-md-7 mt-2">
          <SectionHeader icon={FaHistory} title="Activity History" />
          <div style={{ height: 300 }} className="ag-theme-alpine shadow-sm rounded">
            <AgGridReact
              rowData={activityRows}
              columnDefs={activityColDefs}
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
              pagination={true}
              paginationPageSize={10}
            />
          </div>
        </div>
      </div>

      {/* Bills Section */}
      <div className="row mb-3">
        <div className="col-12">
          <SectionHeader
            icon={FaReceipt}
            title="Bills"
            rightElement={
              <div className="d-flex" style={{ gap: "8px" }}>
                {current?.TotalPendingValue > 0 &&
                current?.Bills?.length > 0 &&
                current?.RetentionRate ? (
                  <ColoredActionButton
                    icon={current?.IsRetention ? FaLockOpen : FaLock}
                    label="Release Retention"
                    variant="warning"
                    onClick={() =>
                      current?.IsRetention
                        ? setShowRetentionModal(true)
                        : toast.warning("Clear all the bills to release retention")
                    }
                  />
                ) : current?.TotalPendingValue === 0 && current?.RetentionRate ? (
                  <ColoredActionButton
                    icon={FaMoneyBillWave}
                    label="View Retention"
                    variant="save"
                    onClick={() => {
                      getTransactions(current?.Id);
                      setOpenTransactionModel(true);
                    }}
                  />
                ) : null}
                <ColoredActionButton
                  icon={FaPlus}
                  label="Add Bill"
                  variant="primary"
                  onClick={() =>
                    history.push(`/works/contractor-bills/add/${current?.Id || id}`, {
                      workOrder: current,
                      returnUrl: history.location.pathname,
                    })
                  }
                />
              </div>
            }
          />
          {current?.Bills?.length === 0 ? (
            <div className="text-center p-4 border rounded bg-light text-secondary">
              No bills created yet.
            </div>
          ) : (
            current?.Bills?.map((b) => {
              const isExpanded = !!expandedBills[b.Id];
              return (
                <div
                  key={b.Id}
                  className="mb-3 overflow-hidden bill-card"
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  }}
                >
                  {/* Accordion Header */}
                  <div
                    className="d-flex align-items-center justify-content-between p-3"
                    style={{
                      backgroundColor: isExpanded ? "#f8fdfd" : "#fff",
                      cursor: "pointer",
                      borderBottom: isExpanded ? "1px solid #e0e0e0" : "none",
                    }}
                    onClick={() => toggleBill(b.Id)}
                  >
                    <div className="d-flex align-items-center" style={{ gap: "40px" }}>
                      <div className="d-flex align-items-center" style={{ width: "220px" }}>
                        <div
                          className="me-2 d-flex align-items-center justify-content-center"
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            backgroundColor: "#e7f4f4",
                            color: "#0b6462",
                          }}
                        >
                          {isExpanded ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
                        </div>
                        <span style={{ fontWeight: 700, color: "#333" }}>
                          {b.BillNumber || "Bill"}
                        </span>
                      </div>
                      <div className="d-none d-md-block">
                        <small className="text-secondary d-block">Date</small>
                        <span style={{ fontSize: "13px", fontWeight: "500" }}>
                          {b.CreatedAt ? moment(b.CreatedAt).format("DD-MMM-YYYY") : "-"}
                        </span>
                      </div>
                      <div className="d-none d-md-block">
                        <small className="text-secondary d-block">Status</small>
                        <StatusBadge status={b.Status} />
                      </div>
                      <div className="d-none d-lg-block">
                        <small className="text-secondary d-block">Cumulative %</small>
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#0b6462",
                          }}
                        >
                          {b.CumulativePercentage}%
                        </span>
                      </div>
                      {b.NetPayable !== undefined && (
                        <div className="d-none d-lg-block">
                          <small className="text-secondary d-block">Net Payable</small>
                          <span
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#0b6462",
                            }}
                          >
                            Rs. {b.NetPayable?.toLocaleString?.()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div
                      className="d-flex align-items-center"
                      style={{ gap: "8px" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {b?.WobApprovals?.find(
                        (a) => a.ApproverUserId === session?.LoginId && a.Status === "Pending",
                      ) && (
                        <ColoredActionButton
                          icon={FaCheck}
                          label="Approve"
                          variant="success"
                          onClick={() => openApprove(b?.WobApprovals)}
                        />
                      )}
                      {/* {b.Status === "Approved" && (
                        <ColoredActionButton
                          icon={MdPayment}
                          label="Pay Now"
                          variant="info"
                          onClick={() => {
                            setSelectedBillId(b.Id);
                            setConfirmationType("payment");
                            setShowConfirmationModal(true);
                          }}
                        />
                      )} */}
                      {b.Status === "Paid" && (
                        <ColoredActionButton
                          icon={FaMoneyBillWave}
                          label="View Transactions"
                          variant="save"
                          onClick={() => {
                            getTransactions(b.Id);
                            setOpenTransactionModel(true);
                          }}
                        />
                      )}
                    </div>
                  </div>
                  {/* Accordion Body */}
                  {isExpanded && (
                    <div className="p-4 bg-white anim-fade-in border-top">
                      <div className="row mb-4">
                        <div className="col-md-3">
                          <small className="text-secondary d-block mb-1">
                            Gross Amount To Date
                          </small>
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              color: "#0b6462",
                            }}
                          >
                            Rs. {b.GrossAmountToDate?.toLocaleString?.() || 0}
                          </span>
                        </div>
                        <div className="col-md-3">
                          <small className="text-secondary d-block mb-1">
                            Previous Gross Amount
                          </small>
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              color: "#0b6462",
                            }}
                          >
                            Rs. {b.PreviousGrossAmount?.toLocaleString?.() || 0}
                          </span>
                        </div>

                        <div className="col-md-3">
                          <small className="text-secondary d-block mb-1">Current Gross</small>
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              color: "#0b6462",
                            }}
                          >
                            Rs. {b.CurrentMonthGross?.toLocaleString?.() || 0}
                          </span>
                        </div>

                        <div className="col-md-3">
                          <small className="text-secondary d-block mb-1">Retention Deducted</small>
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              color: "#0b6462",
                            }}
                          >
                            Rs. {b.RetentionDeducted?.toLocaleString?.() || 0}
                          </span>
                        </div>
                        <div className="col-md-3 mt-2">
                          <small className="text-secondary d-block mb-1">Tax Deducted</small>
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              color: "#0b6462",
                            }}
                          >
                            Rs. {b.TaxDeducted?.toLocaleString?.() || 0}
                          </span>
                        </div>

                        {b.FileUrl && (
                          <div className="col-md-3 mt-2">
                            <small className="text-secondary d-block mb-1">Bill Attachment</small>
                            <a
                              href={b.FileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-info d-inline-flex align-items-center"
                              style={{ gap: "6px" }}
                            >
                              <FaFileAlt size={12} /> View Document
                            </a>
                          </div>
                        )}
                      </div>

                      <div className="mt-2">
                        <h6
                          className="fz-14 mb-3 d-flex align-items-center"
                          style={{ fontWeight: "700", color: "#0b6462" }}
                        >
                          <MdOutlineApproval size={18} className="me-2" /> Approval Timeline
                        </h6>
                        {b.WobApprovals?.length > 0 ? (
                          <div className="table-responsive shadow-sm rounded border">
                            <table className="table table-sm table-hover m-0">
                              <thead style={{ backgroundColor: "#f8f9fa" }}>
                                <tr>
                                  <th className="ps-3 py-2">Approver</th>
                                  <th className="py-2">Status</th>
                                  <th className="py-2">Comments</th>
                                  <th className="py-2">Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {b.WobApprovals.map((a, i) => (
                                  <tr key={i}>
                                    <td className="ps-3 table_data">
                                      {a.ApprovalUserName || a.ApproverUserId}
                                    </td>
                                    <td className="table_data">
                                      <StatusBadge status={a.Status} />
                                    </td>
                                    <td className="table_data">{a.Comments || "-"}</td>
                                    <td className="table_data">
                                      {a.ActionDate
                                        ? moment(a.ActionDate).format("DD-MMM-YYYY")
                                        : "-"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="text-center p-4 border rounded bg-light text-muted fz-13">
                            No approval history found for this bill.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Amendments Section */}
      <div className="row mb-3">
        <div className="col-12">
          <SectionHeader
            icon={FaExchangeAlt}
            title="Amendments"
            rightElement={
              <ColoredActionButton
                icon={FaPlus}
                label="Add Amendment"
                variant="primary"
                onClick={() => setShowAmendmentModal(true)}
              />
            }
          />
          {current?.Amendments?.length === 0 ? (
            <div className="text-center p-4 border rounded bg-light text-secondary">
              No amendments yet.
            </div>
          ) : (
            <div className="table-responsive shadow-sm rounded border">
              <table className="table table-hover m-0">
                <thead style={{ backgroundColor: "#f8f9fa" }}>
                  <tr>
                    <th className="py-3">Sr</th>
                    <th className="py-3">Reason</th>
                    <th className="py-3 text-end">Amended Amount</th>
                    <th className="py-3 text-end">Previous Amount</th>
                    <th className="py-3 text-end">Revised Amount</th>

                    <th className="py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {current?.Amendments?.map((a, i) => (
                    <tr key={a.Id || i}>
                      <td className="table_data">{i + 1}</td>
                      <td className="table_data">{a.Reason || "-"}</td>
                      <td className="table_data text-end">
                        Rs. {a.ChangeAmount?.toLocaleString?.() || 0}
                      </td>
                      <td className="table_data text-end">
                        Rs. {a.PreviousAmount?.toLocaleString?.() || 0}
                      </td>
                      <td className="table_data text-end">
                        Rs. {a.RevisedAmount?.toLocaleString?.() || 0}
                      </td>
                      <td className="table_data">
                        {a.AmendmentDate ? moment(a.AmendmentDate).format("DD-MMM-YYYY") : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showBillModal && (
        <AddBillModal
          isOpen={showBillModal}
          onClose={() => setShowBillModal(false)}
          woData={current}
          onSuccess={async () => {
            setShowBillModal(false);
            await dispatch(getWorkOrderById(id));
          }}
        />
      )}
      {showAmendmentModal && (
        <AddAmendmentModal
          isOpen={showAmendmentModal}
          onClose={() => setShowAmendmentModal(false)}
          woData={current}
          onSuccess={async () => {
            setShowAmendmentModal(false);
            await dispatch(getWorkOrderById(id));
          }}
        />
      )}
      {showRetentionModal && (
        <RetentionReleaseModal
          isOpen={showRetentionModal}
          onClose={() => setShowRetentionModal(false)}
          woData={current}
          onSuccess={async () => {
            setShowRetentionModal(false);
            await dispatch(getWorkOrderById(id));
          }}
        />
      )}
      {showApproveModal && (
        <div
          className="modalscreen"
          style={{ display: "block", height: "100vh", overflow: "auto" }}
        >
          <div className="modal-content-screen1">
            <div className="row sticky-topNew">
              <div className="col-12 page_heading">
                <h4 className="text-center mb-0">Approve Bill</h4>
                <span className="close pr-2" onClick={() => setShowApproveModal(false)}>
                  &times;
                </span>
              </div>
            </div>
            <div className="pl-3 pr-3">
              <div className="mt-3 d-flex">
                <label className="input_label mr-3">Comments (optional)</label>
                <textarea
                  className="input_address"
                  rows={4}
                  value={approveComments}
                  onChange={(e) => setApproveComments(e.target.value)}
                />
              </div>
              <div className="row mt-4">
                <div className="col-12 d-flex justify-content-end" style={{ gap: 8 }}>
                  <ActionButton
                    text="Cancel"
                    variant="cancel"
                    onClick={() => setShowApproveModal(false)}
                  />
                  <ActionButton text="Approve" variant="save" onClick={() => handleApproveBill()} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showConfirmationModal && (
        <DeleteConfirmModal
          open={showConfirmationModal}
          onClose={() => {
            setShowConfirmationModal(false);
            setConfirmationType(null);
          }}
          onConfirm={() => {
            if (confirmationType === "approve") {
              handleApproveBill();
            } else if (confirmationType === "payment") {
              handleAddPayment();
            }
          }}
          message={
            confirmationType === "payment"
              ? "Do you really want to add payment?"
              : "Do you really want to approve this bill?"
          }
          heading={confirmationType === "payment" ? "Add Payment" : "Approve Bill"}
          loading={isLoading}
          actionText={confirmationType === "payment" ? "Add Payment" : "Approve"}
        />
      )}
      {openTransactionModel && (
        <TransactionDetailModal
          title="Transaction Details"
          transactions={generalLedger.transactionData || []}
          onClose={() => setOpenTransactionModel(false)}
          debitTotal={generalLedger.transactionDebitTotal || 0}
          creditTotal={generalLedger.transactionCreditTotal || 0}
          columns={[
            {
              key: "date",
              label: "Submit On",
              accessor: (t) => moment(t?.EntryDate?.slice(0, 10)).format("DD-MMM-YYYY"),
            },
            {
              key: "account",
              label: "Account",
              accessor: (t) => t?.Account?.AccountTitle,
            },
            {
              key: "narration",
              label: "Narration",
              accessor: (t) => t?.Narration,
            },
          ]}
        />
      )}
    </div>
  );
}
